import {
  CSSObject,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  Theme,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';

import { sizes } from '@/theme/variables';

const openedMixin = (theme: Theme): CSSObject => ({
  width: sizes.sidebarOpen,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.standard,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.standard,
  }),
  overflowX: 'hidden',
  width: sizes.sidebarClosed,
});

export const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  width: sizes.sidebarOpen,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': {
          ...openedMixin(theme),
          // Hide scrollbar for all browsers
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE and Edge
          '&::-webkit-scrollbar': {
            // Chrome, Safari, Opera
            display: 'none',
          },
        },
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': {
          ...closedMixin(theme),
          // Hide scrollbar for all browsers
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE and Edge
          '&::-webkit-scrollbar': {
            // Chrome, Safari, Opera
            display: 'none',
          },
        },
      },
    },
  ],
}));

export const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.error.main
      : theme.palette.sideBar.main,
  '&.active': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.error.main
        : theme.palette.sideBar.main,
  },
}));

export const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.common.white
      : theme.palette.sideBar.main,
  '&.active': {
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.common.white
        : theme.palette.common.white,
  },
}));

export const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.common.white
      : theme.palette.sideBar.main,
  '&.active': {
    color: theme.palette.common.white,
  },
  '& .MuiListItemText-primary': {
    fontSize: 15,
    fontWeight: 500,
  },
}));

export const ToggleButton = styled('div')(({ theme }) => ({
  position: 'fixed',
  transform: 'translateY(-50%)',
  width: 38,
  height: 38,
  borderRadius: '50%',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: theme.transitions.create(
    ['opacity', 'background-color', 'box-shadow', 'left', 'top'],
    {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    },
  ),
  opacity: 0,
  zIndex: 1300,
  '& svg': {
    color: theme.palette.text.primary,
  },
}));
